import { MetricInfoDefinition } from '../components/MasterAdmin/ui/MetricInfoPopover';

/** Short explanations for Production Operations dashboard metrics (click info icon). */
export const operationsMetricInfo: Record<string, MetricInfoDefinition> = {
  healthScore: {
    definition:
      'Weighted score (0–100%) combining Cloud Run, Cloud SQL, errors, latency, uptime, external APIs, and deployments. ≥85 healthy, 60–84 warning, below 60 critical.',
    logic: ['Recalculated every 30 seconds from live backend probes and GCP metrics.'],
    sources: [{ system: 'Operations API', table: 'dashboard', columns: ['overall.healthScore'] }],
  },
  serviceHealthPie: {
    definition:
      'Share of monitored subsystems by status: healthy, warning, critical, or unknown. Includes backend, database, APIs, auth, storage, security, and queues.',
    sources: [{ system: 'Operations API', table: 'sections', columns: ['*.status'] }],
  },
  errorCategoryPie: {
    definition:
      'Runtime errors grouped by category (database, API, Firebase, payment, auth, migration, etc.) from Cloud Logging or BigQuery fallback.',
    sources: [
      { system: 'GCP', table: 'Cloud Logging', columns: ['severity', 'textPayload'] },
      { system: 'BigQuery', table: 'error_logs', columns: ['error_type', 'message'] },
    ],
  },
  cloudRunStatus: {
    definition:
      'Current Cloud Run service state (READY, LOCAL_DEV, UNAVAILABLE). On localhost shows LOCAL_DEV until deployed to GCP.',
    sources: [{ system: 'GCP', table: 'Cloud Run Admin API', columns: ['terminalCondition.state'] }],
  },
  databaseConnected: {
    definition:
      'Whether MySQL bootstrap completed and Sequelize can run queries. Booting means DB connection or migrations still initializing.',
    sources: [{ system: 'Backend', table: 'health + SequelizeMeta', columns: ['dbReady', 'bootstrapPhase'] }],
  },
  apiAvailability: {
    definition:
      'Percentage of internal health-probe endpoints returning success (2xx or expected auth responses) in the last check cycle.',
    logic: ['Probes run against /health, /api/doctor, /api/ambulance, login route, etc.'],
    sources: [{ system: 'Operations', table: 'internalApiProbe', columns: ['availabilityPercent'] }],
  },
  uptime24h: {
    definition:
      'Estimated availability from Cloud Run request metrics: (total requests − 5xx) / total requests over 24 hours. Requires GCP Monitoring on Cloud Run.',
    formula: 'Availability % = ((total − 5xx) / total) × 100',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['run.googleapis.com/request_count'] }],
  },
  resourceUtilization: {
    definition:
      'CPU, memory, and storage utilization bars for Cloud Run and Cloud SQL. Values above 70% trigger warnings; above 85% are critical.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['cpu.utilizations', 'memory.utilizations'] }],
  },
  apiLatencyTrend: {
    definition:
      'Response time (ms) from automated probes to each internal API endpoint. Spikes often indicate cold starts, DB slowness, or downstream failures.',
    sources: [{ system: 'Operations', table: 'internalApis.endpoints', columns: ['responseTimeMs'] }],
  },
  recommendations: {
    definition:
      'Auto-generated actions from the recommendations engine based on migrations, CPU/memory thresholds, 5xx spikes, and external service failures.',
    sources: [{ system: 'Operations', table: 'recommendationsEngine', columns: ['message', 'action'] }],
  },
  backendRpm: {
    definition: 'Requests per minute to the Cloud Run service from GCP Monitoring (last hour average).',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['request_count'] }],
  },
  backendLatency: {
    definition: 'Median or latest request latency (ms) reported by Cloud Run. High values affect user-facing API response times.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['request_latencies'] }],
  },
  backendInstances: {
    definition: 'Number of Cloud Run container instances currently serving traffic (auto-scales with load).',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['container/instance_count'] }],
  },
  backend5xx: {
    definition:
      'Server error responses (HTTP 5xx) in the last hour. Any sustained 5xx indicates application or infrastructure failure.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['request_count', 'response_code_class=5xx'] }],
  },
  cloudRunResources: {
    definition: 'Container CPU and memory utilization for the active Cloud Run revision.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['cpu.utilizations', 'memory.utilizations'] }],
  },
  httpErrors: {
    definition: 'Client (4xx) vs server (5xx) error counts from Cloud Run request metrics in the last hour.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['4xx', '5xx'] }],
  },
  backendStatus: {
    definition:
      'Overall backend health derived from Cloud Run metrics, error rates, and local process state when running on localhost.',
    logic: ['healthy: normal load and no critical 5xx', 'warning: elevated 4xx/5xx or high CPU', 'critical: outage or startup failure'],
    sources: [{ system: 'Operations', table: 'sections.backend', columns: ['status'] }],
  },
  dbQueryTime: {
    definition: 'Time for a simple SELECT 1 probe through Sequelize to MySQL. Spikes indicate connection pool or DB load issues.',
    sources: [{ system: 'MySQL', table: 'live probe', columns: ['queryResponseTimeMs'] }],
  },
  dbConnections: {
    definition: 'Active connections to Cloud SQL from GCP Monitoring. Near max connections can cause API timeouts.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['network/connections'] }],
  },
  dbDeadlocks: {
    definition: 'InnoDB deadlock counter from MySQL status. Non-zero values mean concurrent transactions conflicted.',
    sources: [{ system: 'MySQL', table: 'SHOW STATUS', columns: ['Innodb_deadlocks'] }],
  },
  pendingMigrations: {
    definition:
      'Sequelize migration files in /migrations not yet recorded in SequelizeMeta. Run db:migrate before deploy to avoid schema errors.',
    sources: [
      { system: 'Filesystem', table: 'migrations/', columns: ['*.js'] },
      { system: 'MySQL', table: 'SequelizeMeta', columns: ['name'] },
    ],
  },
  cloudSqlUtilization: {
    definition: 'Cloud SQL CPU, memory, and disk utilization from GCP Monitoring.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['cloudsql.googleapis.com/database/*'] }],
  },
  slowQueries: {
    definition:
      'Top slow statements from performance_schema (if enabled). Helps identify queries needing indexes or optimization.',
    sources: [{ system: 'MySQL', table: 'performance_schema.events_statements_summary_by_digest', columns: ['DIGEST_TEXT', 'AVG_TIMER_WAIT'] }],
  },
  errorMix: {
    definition: 'Distribution of grouped runtime errors by category for the selected time range and filters.',
    sources: [{ system: 'GCP / BigQuery', table: 'error logs', columns: ['category', 'message'] }],
  },
  errorList: {
    definition:
      'Grouped errors with occurrence count, latest timestamp, severity, and message. Same root cause is merged by category + message prefix.',
    sources: [{ system: 'Operations', table: 'runtimeErrors.grouped', columns: ['occurrences', 'latestTimestamp'] }],
  },
  liveLogs: {
    definition:
      'Recent log lines from Cloud Logging (production) or BigQuery error table (local fallback). Search filters message text.',
    sources: [{ system: 'GCP', table: 'Cloud Logging', columns: ['timestamp', 'severity', 'textPayload'] }],
  },
  endpointLatency: {
    definition: 'Bar chart of probe response times per internal endpoint. Color reflects healthy (green), warning (amber), or down (red).',
    sources: [{ system: 'Operations', table: 'internalApis.endpoints', columns: ['name', 'responseTimeMs', 'status'] }],
  },
  internalApiTable: {
    definition: 'Automated HTTP probes to core routes. Expected 401/404 on some routes counts as healthy if configured.',
    sources: [{ system: 'Operations', table: 'internalApiProbe', columns: ['path', 'method', 'httpStatus'] }],
  },
  externalHealthPie: {
    definition: 'Health check results for third-party services: Firebase, Razorpay, Brevo, WhatsApp, Redis, VideoSDK, etc.',
    sources: [{ system: 'Operations', table: 'externalServices.services', columns: ['status', 'latencyMs'] }],
  },
  externalLatency: {
    definition: 'Round-trip time (ms) for lightweight HTTP probes to external provider APIs.',
    sources: [{ system: 'Operations', table: 'externalServices', columns: ['latencyMs'] }],
  },
  externalServicesTable: {
    definition: 'Per-service status, last success/failure, and error message from live connectivity probes.',
    sources: [{ system: 'Operations', table: 'externalServices.services', columns: ['name', 'status', 'errorMessage'] }],
  },
  authChecks: {
    definition: 'Validates JWT sign/verify, refresh secret presence, Firebase Admin auth reachability, and token expiry policy.',
    sources: [{ system: 'Backend', table: 'authHealth', columns: ['checks'] }],
  },
  credentials: {
    definition: 'Whether critical secrets (JWT, DB, Razorpay, Firebase) loaded from env or Secret Manager at boot.',
    sources: [{ system: 'Secret Manager / env', table: 'credentialChecks', columns: ['name', 'loaded'] }],
  },
  notificationChannels: {
    definition: 'Stacked counts of push, email, SMS, and WhatsApp notifications: pending queue, sent in 24h, and failed in 24h.',
    sources: [{ system: 'MySQL', table: 'notification logs', columns: ['status'] }],
  },
  notificationPending: {
    definition: 'Notifications queued but not yet delivered across all channels.',
    sources: [{ system: 'MySQL', table: 'notification queues', columns: ['status=PENDING'] }],
  },
  notificationSent: {
    definition: 'Successfully sent notifications in the last 24 hours.',
    sources: [{ system: 'MySQL', table: 'notification logs', columns: ['status=SENT', 'createdAt'] }],
  },
  notificationFailed: {
    definition: 'Failed delivery attempts in the last 24 hours — investigate provider errors or invalid tokens.',
    sources: [{ system: 'MySQL', table: 'notification logs', columns: ['status=FAILED'] }],
  },
  schedulerJobs: {
    definition: 'Cloud Scheduler–triggered jobs (reminder engine, observability rollup, WhatsApp campaigns, etc.) and their enabled state.',
    sources: [{ system: 'Backend', table: 'schedulerHealth.jobs', columns: ['name', 'schedule', 'enabled'] }],
  },
  queueDepth: {
    definition: 'Pending, running, and failed items in background queues (e.g. WhatsApp retargeting, prescription queue).',
    sources: [{ system: 'MySQL', table: 'queue tables', columns: ['status'] }],
  },
  storageHealth: {
    definition: 'Firebase Storage bucket reachability and upload latency from Admin SDK probe.',
    sources: [{ system: 'Firebase', table: 'Storage', columns: ['bucket', 'getMetadata'] }],
  },
  storageProvider: {
    definition: 'Object storage backend used for file uploads (typically Firebase Storage).',
    sources: [{ system: 'Operations', table: 'storage', columns: ['provider'] }],
  },
  storageBucket: {
    definition: 'Firebase Storage bucket name holding user uploads and health records.',
    sources: [{ system: 'Firebase', table: 'Storage', columns: ['bucket'] }],
  },
  storageLatency: {
    definition: 'Round-trip time (ms) for a metadata probe against the storage bucket.',
    sources: [{ system: 'Operations', table: 'storage', columns: ['latencyMs'] }],
  },
  httpErrorsChart: {
    definition: 'Client (4xx) vs server (5xx) error counts from Cloud Run request metrics in the last hour.',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['4xx', '5xx'] }],
  },
  topErrorsBar: {
    definition: 'Most frequent error messages by occurrence count in the selected period.',
    sources: [{ system: 'Operations', table: 'errorAnalytics.groupedByFrequency', columns: ['message', 'occurrences'] }],
  },
  liveLogsViewer: {
    definition: 'Recent log lines from Cloud Logging (production) or BigQuery error table (local fallback). Search filters message text.',
    sources: [{ system: 'GCP', table: 'Cloud Logging', columns: ['timestamp', 'severity', 'textPayload'] }],
  },
  errorAnalyticsPie: {
    definition: "Today's errors broken down by category for trend analysis and prioritization.",
    sources: [{ system: 'Operations', table: 'errorAnalytics.byCategory', columns: ['category', 'count'] }],
  },
  deployments: {
    definition: 'Cloud Run revision history with commit SHA and deploy time. Rollback shifts 100% traffic to a prior revision.',
    sources: [{ system: 'GCP', table: 'Cloud Run revisions', columns: ['name', 'createTime', 'labels'] }],
  },
  uptimeDonut: {
    definition: 'Availability percentage for 24h, 7d, or 30d windows based on Cloud Run 5xx ratio. Empty on localhost without GCP.',
    formula: '((total requests − 5xx) / total) × 100',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['request_count'] }],
  },
  statusHealthy: {
    definition: 'All checks passed or metrics within normal thresholds. No immediate action required.',
    sources: [{ system: 'Operations', table: 'healthScore', columns: ['status'] }],
  },
  statusWarning: {
    definition: 'Degraded performance or elevated errors. Review recommendations and related metrics.',
    sources: [{ system: 'Operations', table: 'healthScore', columns: ['status'] }],
  },
  statusCritical: {
    definition: 'Service outage, high 5xx, failed dependencies, or missing migrations. Immediate investigation recommended.',
    sources: [{ system: 'Operations', table: 'healthScore', columns: ['status'] }],
  },
  statusUnknown: {
    definition: 'Metric unavailable (often local dev without GCP credentials or probe timeout).',
    sources: [{ system: 'Operations', table: 'meta', columns: ['gcpAvailable', 'localMode'] }],
  },
  totalCostNet: {
    definition:
      'Final billed amount for the selected period after credits and discounts: Gross Cost − Credits. This is the closest figure to what appears on the invoice.',
    formula: 'Net Cost = Gross Cost − Credits',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['cost', 'credits'] }],
  },
  totalGrossCost: {
    definition: 'Raw list cost of GCP usage before promotional credits, sustained-use discounts, and committed-use savings.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['cost'] }],
  },
  totalCredits: {
    definition: 'Total credits and discounts applied in the period (promotions, CUDs, SUDs). Displayed as savings against gross cost.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['credits'] }],
  },
  avgDailyCost: {
    definition: 'Average net cost per day in the selected range. Useful for spotting whether spend is running above a typical day.',
    formula: 'Avg daily = Net Cost / number of days with data',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['cost', 'usage_start_time'] }],
  },
  totalServicesUsed: {
    definition: 'Count of distinct GCP services that incurred a non-zero net cost in the selected period (Cloud Run, Cloud SQL, Maps, etc.).',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.id'] }],
  },
  uniqueResources: {
    definition: 'Count of unique billable resources (instances, buckets, APIs, databases) that generated cost in the period.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['resource.name'] }],
  },
  savingsRate: {
    definition: 'Share of gross cost offset by credits. Higher means discounts are covering more of the list price.',
    formula: 'Savings rate = |Credits| / Gross Cost × 100',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['cost', 'credits'] }],
  },
  costConcentration: {
    definition: 'Share of net cost coming from the top 3 services. High concentration means a few products dominate the bill.',
    formula: 'Top-3 share = SUM(top 3 service net cost) / Total net cost × 100',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.description', 'cost'] }],
  },
  billingTrend: {
    definition: 'Daily net vs gross spend over the selected period. Spikes often map to traffic, batch jobs, or a new GCP product turning on.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['usage_start_time', 'cost', 'credits'] }],
  },
  costDistribution: {
    definition: 'How net cost is split across GCP services. “Others” groups remaining services after the top contributors.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.description', 'net_cost'] }],
  },
  topServicesChart: {
    definition: 'Highest-spend GCP services by net cost. Compare bar length to see which products to investigate first.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.description', 'net_cost'] }],
  },
  topServicesTable: {
    definition:
      'Service-level bill: gross, credits, net, share of total, usage units, and growth vs the previous period of the same length.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.id', 'cost', 'credits', 'usage'] }],
  },
  topResourcesTable: {
    definition: 'Specific resources (VM, database, bucket, API) driving the most cost, with parent service and share of the bill.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['resource.name', 'sku.description', 'cost'] }],
  },
  dailyCostTable: {
    definition: 'Day-by-day gross, credits, and net. Days above the period average are flagged so spend spikes are easy to spot.',
    sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['usage_date', 'cost', 'credits'] }],
  },
}

export const operationsColumnInfo: Record<string, MetricInfoDefinition> = {
  time: { definition: 'Timestamp of the latest occurrence for this grouped error or log entry.', sources: [{ system: 'Logs', table: 'timestamp', columns: ['timestamp'] }] },
  severity: { definition: 'Log severity: ERROR, CRITICAL, WARNING, etc.', sources: [{ system: 'GCP', table: 'Logging', columns: ['severity'] }] },
  category: { definition: 'Auto-classified error domain: database, api, firebase, payment, auth, migration…', sources: [{ system: 'Operations', table: 'categorizeError', columns: ['category'] }] },
  message: { definition: 'Error message or log text (truncated in table).', sources: [{ system: 'Logs', table: 'message', columns: ['message'] }] },
  count: { definition: 'Number of times this error pattern occurred in the selected range.', sources: [{ system: 'Operations', table: 'grouped', columns: ['occurrences'] }] },
  endpoint: { definition: 'Human-readable name of the probed API route.', sources: [{ system: 'Operations', table: 'internalApis', columns: ['name', 'path'] }] },
  method: { definition: 'HTTP method used for the health probe (GET/POST).', sources: [{ system: 'Operations', table: 'internalApis', columns: ['method'] }] },
  status: { definition: 'Health status: healthy, warning, critical, or down based on probe result and thresholds.', sources: [{ system: 'Operations', table: '*.status', columns: ['status'] }] },
  latency: { definition: 'Response time in milliseconds for the probe or external API call.', sources: [{ system: 'Operations', table: '*.latencyMs', columns: ['latencyMs'] }] },
  http: { definition: 'HTTP status code returned by the probed endpoint.', sources: [{ system: 'Operations', table: 'internalApis', columns: ['httpStatus'] }] },
  availability: { definition: '100% if probe succeeded; 0% if failed for this endpoint.', sources: [{ system: 'Operations', table: 'internalApis', columns: ['availability'] }] },
  service: { definition: 'Third-party integration name (Firebase, Razorpay, Brevo, etc.).', sources: [{ system: 'Operations', table: 'externalServices', columns: ['name'] }] },
  revision: { definition: 'Cloud Run revision identifier (immutable deploy unit).', sources: [{ system: 'GCP', table: 'Cloud Run', columns: ['revision'] }] },
  commit: { definition: 'Git commit SHA label attached to the revision at deploy time.', sources: [{ system: 'GCP', table: 'revision labels', columns: ['commit_sha'] }] },
  channel: { definition: 'Notification delivery channel: push, email, sms, whatsapp.', sources: [{ system: 'MySQL', table: 'notifications', columns: ['channel'] }] },
  pending: { definition: 'Items waiting to be processed or sent.', sources: [{ system: 'MySQL', table: 'queues', columns: ['status=PENDING'] }] },
  sent: { definition: 'Successfully delivered in the last 24 hours.', sources: [{ system: 'MySQL', table: 'logs', columns: ['status=SENT'] }] },
  failed: { definition: 'Delivery failures in the last 24 hours.', sources: [{ system: 'MySQL', table: 'logs', columns: ['status=FAILED'] }] },
  job: { definition: 'Scheduled job name (reminder engine, observability rollup, campaigns…).', sources: [{ system: 'Operations', table: 'scheduler', columns: ['jobs'] }] },
  schedule: { definition: 'How often Cloud Scheduler invokes this job.', sources: [{ system: 'Cloud Scheduler', table: 'jobs', columns: ['schedule'] }] },
  enabled: { definition: 'Whether the job is active via env flag or scheduler config.', sources: [{ system: 'Operations', table: 'scheduler', columns: ['enabled'] }] },
  queue: { definition: 'Background processing queue name.', sources: [{ system: 'MySQL', table: 'queues', columns: ['name'] }] },
  running: { definition: 'Items currently being processed.', sources: [{ system: 'MySQL', table: 'queues', columns: ['status=PROCESSING'] }] },
  credential: { definition: 'Secret or API key required for platform operation.', sources: [{ system: 'Secret Manager', table: 'secrets', columns: ['name'] }] },
  loaded: { definition: 'Whether the credential was successfully loaded at application boot.', sources: [{ system: 'Operations', table: 'security', columns: ['loaded'] }] },
  field: { definition: 'Metadata field name for the backend or deployment record.', sources: [{ system: 'Operations', table: 'backend', columns: ['*'] }] },
  value: { definition: 'Current value for this metadata field.', sources: [{ system: 'Operations', table: 'backend', columns: ['*'] }] },
  slowQuery: { definition: 'SQL digest text from performance_schema (truncated).', sources: [{ system: 'MySQL', table: 'performance_schema', columns: ['DIGEST_TEXT'] }] },
  executions: { definition: 'How many times this query pattern executed.', sources: [{ system: 'MySQL', table: 'performance_schema', columns: ['COUNT_STAR'] }] },
  avgMs: { definition: 'Average execution time in milliseconds for this query pattern.', sources: [{ system: 'MySQL', table: 'performance_schema', columns: ['AVG_TIMER_WAIT'] }] },
  action: { definition: 'Rollback sends 100% traffic to a previous Cloud Run revision.', sources: [{ system: 'GCP', table: 'Cloud Run', columns: ['traffic'] }] },
  details: { definition: 'Additional context from the health check (error message or config note).', sources: [{ system: 'Operations', table: 'checks', columns: ['message'] }] },
  created: { definition: 'When this Cloud Run revision was deployed.', sources: [{ system: 'GCP', table: 'Cloud Run', columns: ['createTime'] }] },
};

export const operationsTabInfo: Record<string, MetricInfoDefinition> = {
  overview: {
    definition: 'Command center for platform health: score, service map, resource usage, API latency, and automated recommendations.',
    sources: [{ system: 'Operations API', table: '/operations/dashboard', columns: ['overall', 'sections'] }],
  },
  backend: {
    definition: 'Cloud Run service metrics, HTTP errors, instances, and deployment metadata for the Node.js API.',
    sources: [{ system: 'GCP', table: 'Cloud Run + Monitoring', columns: ['*'] }],
  },
  database: {
    definition: 'MySQL / Cloud SQL health: connections, query time, migrations, slow queries, and utilization.',
    sources: [{ system: 'MySQL + GCP', table: 'Cloud SQL', columns: ['*'] }],
  },
  errors: {
    definition: 'Production runtime errors grouped by message with filters for time range and category.',
    sources: [{ system: 'GCP / BigQuery', table: 'error logs', columns: ['*'] }],
  },
  apis: {
    definition: 'Synthetic monitors that hit critical internal routes and report latency and availability.',
    sources: [{ system: 'Operations', table: 'internalApiProbe', columns: ['*'] }],
  },
  external: {
    definition: 'Connectivity and latency checks for Firebase, payments, messaging, maps, and other vendors.',
    sources: [{ system: 'Operations', table: 'externalServicesProbe', columns: ['*'] }],
  },
  auth: {
    definition: 'JWT, refresh tokens, and Firebase Auth configuration sanity checks.',
    sources: [{ system: 'Backend', table: 'authHealth', columns: ['*'] }],
  },
  notifications: {
    definition: 'Cross-channel notification pipeline stats: pending, sent, and failed volumes.',
    sources: [{ system: 'MySQL', table: 'notification logs', columns: ['*'] }],
  },
  scheduler: {
    definition: 'Cron and Cloud Scheduler jobs that drive reminders, campaigns, and rollups.',
    sources: [{ system: 'Cloud Scheduler', table: 'jobs', columns: ['*'] }],
  },
  queue: {
    definition: 'Async work queues for retargeting, prescriptions, and similar background processing.',
    sources: [{ system: 'MySQL', table: 'queue tables', columns: ['*'] }],
  },
  storage: {
    definition: 'Firebase Storage bucket health used for uploads and health records.',
    sources: [{ system: 'Firebase', table: 'Storage', columns: ['*'] }],
  },
  security: {
    definition: 'SSL, JWT expiry, loaded credentials, and Secret Manager visibility.',
    sources: [{ system: 'Secret Manager', table: 'secrets', columns: ['*'] }],
  },
  analytics: {
    definition: 'Error frequency analytics for prioritizing fixes by category and volume.',
    sources: [{ system: 'Operations + BigQuery', table: 'errorAnalytics', columns: ['*'] }],
  },
  logs: {
    definition: 'Near-real-time log tail with search and JSON export for incident debugging.',
    sources: [{ system: 'GCP', table: 'Cloud Logging', columns: ['*'] }],
  },
  deployments: {
    definition: 'Revision history and one-click rollback for Cloud Run releases.',
    sources: [{ system: 'GCP', table: 'Cloud Run Admin', columns: ['revisions'] }],
  },
  uptime: {
    definition: 'Historical availability slices for SLA tracking (24h, 7d, 30d).',
    sources: [{ system: 'GCP', table: 'Monitoring', columns: ['request_count', '5xx'] }],
  },
};

export function getOperationsMetricInfo(key: string): MetricInfoDefinition | undefined {
  return operationsMetricInfo[key];
}

export function getOperationsColumnInfo(key: string): MetricInfoDefinition | undefined {
  return operationsColumnInfo[key];
}
